import { Timestamp, type DocumentData } from 'firebase/firestore';
import type {
  ClientKey,
  Deployment,
  Estate,
  RouterTokenRecord,
  RouterUsage,
  TenantSummary,
  TokenUsage,
  UsageRow,
} from '../../data/types';

/* The document → view-model boundary of the live lane, kept apart from the
 * watch plumbing so it can be tested without a Firestore: every field a
 * screen reads is mapped here, with its absence mapped deliberately. */

/** iso normalises Firestore's Timestamp to the ISO strings the model uses.
 *
 *  The boundary is here on purpose: every store above this line assumes a
 *  string, so a Timestamp leaking past it would sort and render wrongly rather
 *  than fail. */
function iso(v: unknown): string {
  if (v instanceof Timestamp) return v.toDate().toISOString();
  if (v instanceof Date) return v.toISOString();
  return typeof v === 'string' ? v : '';
}

/** isoOrUndefined keeps an absent optional timestamp absent, rather than
 *  turning it into the epoch. */
function isoOrUndefined(v: unknown): string | undefined {
  const s = iso(v);
  return s === '' ? undefined : s;
}

export function mapEstate(id: string, d: DocumentData): Estate {
  return {
    id: (d.id as string) || id,
    displayName: (d.displayName as string) ?? '',
    enabled: Boolean(d.enabled),
    realms: (d.realms as Estate['realms']) ?? [],
    providers: (d.providers as Estate['providers']) ?? { allowByoKeys: false },
    pool: (d.pool as Estate['pool']) ?? { enabled: false, useShared: false },
    limits: (d.limits as Estate['limits']) ?? {
      attributedPerMinute: 0,
      billablePerMinute: 0,
    },
    billing: (d.billing as Estate['billing']) ?? { eventLane: 'event_key', eventType: '' },
    // Absent reads as off. A capture policy that failed to map must not render
    // as "storing", and it must not render as "off" for an estate that IS
    // storing either — which is why this is mapped rather than left to
    // whatever the document happened to carry.
    capture: (d.capture as Estate['capture']) ?? { mode: '' },
    // Likewise memory: absent is off, and a missing block never renders as
    // "unknown" — the router reads it the same way.
    memory: (d.memory as Estate['memory']) ?? { mode: '' },
    createdAt: iso(d.createdAt),
    updatedAt: iso(d.updatedAt),
  };
}

export function mapDeployment(id: string, d: DocumentData): Deployment {
  return {
    // The logical id is a FIELD; the document id is a digest. Preferring the
    // field keeps "am/acme@cli" on screen instead of a hash.
    id: (d.id as string) || id,
    estateId: (d.estateId as string) ?? '',
    tenantId: (d.tenantId as string) ?? '',
    label: (d.label as string) ?? '',
    enabled: Boolean(d.enabled),
    providerAllow: d.providerAllow as string[] | undefined,
    limits: d.limits as Deployment['limits'],
    createdBy: (d.createdBy as string) ?? '',
    createdAt: iso(d.createdAt),
    updatedAt: iso(d.updatedAt),
  };
}

export function mapToken(id: string, d: DocumentData): RouterTokenRecord {
  return {
    tokenId: (d.tokenId as string) || id,
    estateId: (d.estateId as string) ?? '',
    tenantId: (d.tenantId as string) ?? '',
    deploymentId: (d.deploymentId as string) ?? '',
    tokenSha256: d.tokenSha256 as string | undefined,
    issuedAt: iso(d.issuedAt),
    expiresAt: iso(d.expiresAt),
    revokedAt: isoOrUndefined(d.revokedAt),
    billingMode: d.billingMode as RouterTokenRecord['billingMode'],
  };
}

export function mapClientKey(id: string, d: DocumentData): ClientKey {
  return {
    id: (d.id as string) || id,
    publicJwk: (d.publicJwk as string) ?? '',
    estateId: (d.estateId as string) ?? '',
    tenantId: (d.tenantId as string) ?? '',
    deploymentId: (d.deploymentId as string) ?? '',
    label: (d.label as string) ?? '',
    enabled: d.enabled === undefined ? true : Boolean(d.enabled),
    expiresAt: isoOrUndefined(d.expiresAt),
    revokedAt: isoOrUndefined(d.revokedAt),
    lastUsedAt: isoOrUndefined(d.lastUsedAt),
    createdBy: (d.createdBy as string) ?? '',
    createdAt: iso(d.createdAt),
    updatedAt: iso(d.updatedAt),
  };
}

/** mapTenant projects a registry record onto the console's tenant view model.
 *
 *  The registry carries identity and the shard ordinal, deliberately and only:
 *  billing mode belongs to the token record (DR-13), and deployment and key
 *  counts are joins the screens already have the collections to make. The
 *  fields it cannot know are left at their zero rather than guessed. */
export function mapTenant(id: string, d: DocumentData): TenantSummary {
  const estateId = (d.estateId as string) ?? '';
  const tenantId = (d.tenantId as string) ?? '';
  return {
    key: `${estateId}/${tenantId}` as TenantSummary['key'],
    estateId,
    tenantId,
    displayName: (d.displayName as string) || tenantId || id,
    deployments: 0,
    sealedKeys: 0,
    billingMode: 'billable',
    status: 'active',
    lane: 'composed',
    listCostUsd: null,
  };
}

function mapRouter(d: DocumentData, tokens: (v: unknown) => TokenUsage): RouterUsage {
  const f = d.forecast as DocumentData | undefined;
  return {
    provider: (d.provider as string) ?? '',
    requestModel: (d.requestModel as string) ?? '',
    servedModel: d.servedModel as string | undefined,
    poolKeyId: d.poolKeyId as string | undefined,
    latencyMs: (d.latencyMs as number) ?? 0,
    usage: tokens(d.usage),
    forecast: f
      ? {
          card: (f.card as string) ?? '',
          rule: f.rule as string | undefined,
          boundary: (f.boundary as string) ?? '',
          pSolve: (f.pSolve as number) ?? 0,
          task: f.task as string | undefined,
        }
      : undefined,
  };
}

export function mapUsage(id: string, d: DocumentData): UsageRow {
  const tokens = (v: unknown) => (v as UsageRow['primary']) ?? {
    modelId: '', inputTokens: 0, cachedTokens: 0,
    cacheCreationTokens: 0, outputTokens: 0, finishReason: '', retryReason: '',
  };
  return {
    // UsageRow.ID is json:"-" in Go — it is the document key, and a snapshot
    // supplies it natively. This is why the watch lane has no gap here.
    id,
    estateId: (d.estateId as string) ?? '',
    tenantId: (d.tenantId as string) ?? '',
    eventId: (d.eventId as string) ?? '',
    lane: (d.lane as UsageRow['lane']) ?? '',
    requestId: (d.requestId as string) ?? '',
    deploymentId: (d.deploymentId as string) ?? '',
    subject: (d.subject as string) ?? '',
    authLane: (d.authLane as UsageRow['authLane']) ?? 'token',
    billingMode: (d.billingMode as string) ?? '',
    billable: Boolean(d.billable),
    workId: (d.workId as string) ?? '',
    agentName: (d.agentName as string) ?? '',
    callKey: (d.callKey as string) ?? '',
    seq: (d.seq as number) ?? 0,
    workstreamId: (d.workstreamId as string) ?? '',
    seatId: (d.seatId as string) ?? '',
    subjectRef: (d.subjectRef as string) ?? '',
    taskType: (d.taskType as string) ?? '',
    provider: (d.provider as string) ?? '',
    requestModel: (d.requestModel as string) ?? '',
    servedModel: (d.servedModel as string) ?? '',
    status: (d.status as UsageRow['status']) ?? 'ok',
    latencyMs: (d.latencyMs as number) ?? 0,
    poolKeyId: d.poolKeyId as string | undefined,
    primary: tokens(d.primary),
    refused: d.refused ? tokens(d.refused) : undefined,
    // Routing fields are absent on every row written before routing existed
    // and on every unrouted row since: absent maps to absent, never to ''.
    routedFrom: d.routedFrom as string | undefined,
    routeTier: d.routeTier as string | undefined,
    routeReason: d.routeReason as string | undefined,
    router: d.router ? mapRouter(d.router as DocumentData, tokens) : undefined,
    // The two units: absent on a completion, present on the row that spent
    // them. Absent maps to absent so rowKind reads a completion honestly.
    search: d.search
      ? {
          provider: String((d.search as DocumentData).provider ?? ''),
          queries: Number((d.search as DocumentData).queries ?? 0),
          results: Number((d.search as DocumentData).results ?? 0),
        }
      : undefined,
    memory: d.memory
      ? {
          op: String((d.memory as DocumentData).op ?? ''),
          items: Number((d.memory as DocumentData).items ?? 0),
          hits: Number((d.memory as DocumentData).hits ?? 0),
        }
      : undefined,
    compact: d.compact
      ? {
          op: String((d.compact as DocumentData).op ?? ''),
          folds: Number((d.compact as DocumentData).folds ?? 0),
          tokensBefore: Number((d.compact as DocumentData).tokensBefore ?? 0),
          tokensAfter: Number((d.compact as DocumentData).tokensAfter ?? 0),
        }
      : undefined,
    occurredAt: iso(d.occurredAt),
    expireAt: iso(d.expireAt),
    conflictOf: d.conflictOf as string | undefined,
  };
}
